import { useAtomValue, useSetAtom } from 'jotai';

import { TEAM_MEMBER_STATUS, type TeamMemberStatus } from '@ekhodealer/ekho-common';
import { Button, Group, Stack, type TabItem, Tabs, Text } from '@ekhodealer/ekho-common/rev';

import { ExportPopup } from '@common/components/ExportPopup/ExportPopup';

import { useExportTeamMembers } from '../api/useExportTeamMembers';
import { useSearchTeamMembers } from '../api/useSearchTeamMembers';
import {
	selectedTeamMembersAtom,
	teamParamsAtom,
	teamTabFilterAtom,
	updateTeamTabFilterAtom,
} from '../atoms/teamAtoms';
import { useTeamTableTabCounts } from '../hooks/useTeamTableTabCounts';

import { AddMemberDrawer } from './AddMemberDrawer';

const TAB_LABELS: Record<TeamMemberStatus, string> = {
	[TEAM_MEMBER_STATUS.ALL]: 'All',
	[TEAM_MEMBER_STATUS.ACTIVE]: 'Active',
	[TEAM_MEMBER_STATUS.PENDING]: 'Pending invites',
	[TEAM_MEMBER_STATUS.INACTIVE]: 'Inactive',
};

export function TeamToolbar() {
	const activeTab = useAtomValue(teamTabFilterAtom);
	const updateTabFilter = useSetAtom(updateTeamTabFilterAtom);

	// Export data
	const teamParams = useAtomValue(teamParamsAtom);
	const { data } = useSearchTeamMembers(teamParams);
	const selectedTeamMembers = useAtomValue(selectedTeamMembersAtom);
	const exportMutation = useExportTeamMembers();

	// Tab counts
	const getTabCount = useTeamTableTabCounts();

	const tabItems: TabItem[] = Object.values(TEAM_MEMBER_STATUS).map((tab: TeamMemberStatus) => {
		const count = getTabCount(tab);
		return {
			id: tab,
			content: (
				<Group itemsSpacing='8' itemsAlignY='center' noWrap>
					<Group.Item>
						<span>{TAB_LABELS[tab]}</span>
					</Group.Item>
					<Group.Item>
						<Text size='footnote' color='secondary'>
							{count}
						</Text>
					</Group.Item>
				</Group>
			),
		};
	});

	const handleTabSelect = (item: TabItem): void => {
		updateTabFilter(item.id as TeamMemberStatus);
	};

	return (
		<Stack itemsSpacing='16'>
			<Tabs items={tabItems} selectedKey={activeTab} onSelect={handleTabSelect} />
			<Group itemsAlignY='center' itemsAlignX='end' noWrap>
				<Group itemsSpacing='8' noWrap>
					<ExportPopup
						resourceName='members'
						currentPageItems={data?.data ?? []}
						totalCount={data?.count ?? 0}
						selectedItems={selectedTeamMembers}
						getItemId={(item) => item.id}
						onExport={(ids) => exportMutation.mutateAsync({ memberIds: ids })}
						isPending={exportMutation.isPending}>
						<Button variant='secondary' size='medium'>
							Export
						</Button>
					</ExportPopup>
					<AddMemberDrawer />
				</Group>
			</Group>
		</Stack>
	);
}
